import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Snackbar,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SaveIcon from '@mui/icons-material/Save';

const DARK_GREEN = '#1D4434';

const GuardrailDataUploadTab = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const selectFile = f => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    setFile(f);
    setError(null);
  };

  const handleFileSelect = e => {
    const f = e.target.files?.[0];
    if (f) selectFile(f);
    e.target.value = '';
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) selectFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('excelFile', file);

      const res = await fetch('/api/guardrail/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Upload failed');

      setSnackMsg(`${data.totalRecords} client records saved to database.`);
      setSnackOpen(true);
      setFile(null);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatSize = bytes => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: DARK_GREEN, fontWeight: 600, mb: 0.5 }}>
        Data Upload
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload the <strong>Collaborate_Client_Metrics</strong> Excel file (.xlsx / .xls).
        Saving will replace all existing records in the database and update the Dashboard.
      </Typography>

      {/* Drop zone */}
      <Paper
        elevation={0}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !uploading && inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? DARK_GREEN : file ? 'rgba(29,68,52,0.4)' : 'rgba(0,0,0,0.18)',
          borderRadius: 2,
          p: { xs: 5, sm: 8 },
          mb: 3,
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          bgcolor: dragOver
            ? 'rgba(29,68,52,0.08)'
            : file
            ? 'rgba(29,68,52,0.04)'
            : 'background.default',
          transition: 'all 0.2s ease',
          '&:hover': !uploading
            ? { borderColor: DARK_GREEN, bgcolor: 'rgba(29,68,52,0.04)' }
            : {},
          userSelect: 'none',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {file ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
            <InsertDriveFileIcon sx={{ fontSize: 52, color: DARK_GREEN }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: DARK_GREEN }}>
              {file.name}
            </Typography>
            <Chip label={formatSize(file.size)} size="small" />
            <Typography variant="caption" color="text.secondary">
              Click to replace file
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
            <CloudUploadIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
            <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
              Drop your Excel file here, or click to select
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Supports .xlsx and .xls — replaces all existing data on save
            </Typography>
          </Box>
        )}
      </Paper>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Uploading and parsing…
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {file && !uploading && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleUpload}
            sx={{ bgcolor: DARK_GREEN, '&:hover': { bgcolor: '#2D6A4F' } }}
          >
            Save to Database
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackOpen(false)} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GuardrailDataUploadTab;
